import { ParsedUrlQuery } from 'querystring';
import { ApolloClient } from '@apollo/client';
import {
  CartStatus,
  CreateAndReturnCartMutation,
  CreateAndReturnCartMutationVariables,
  CreateAndReturnCartWithPreviewDiscountMutation,
  CreateAndReturnCartWithPreviewDiscountMutationVariables,
  GetCartByIdQuery,
  GetCartByIdQueryVariables,
  GetCartByIdWithPreviewFunnelDiscountQuery,
  GetCartByIdWithPreviewFunnelDiscountQueryVariables,
} from 'src/ts/@types/generated/graphql/cart-order-service/graphql';
import {
  GET_CART_BY_ID,
  GET_CART_BY_ID_WITH_PREVIEW_FUNNEL_DISCOUNT,
} from 'src/graphql-operations/cart-order-service/carts.query';
import { UserContextInterface } from 'src/context/user';
import {
  CREATE_AND_RETURN_CART,
  CREATE_AND_RETURN_CART_WITH_PREVIEW_DISCOUNT,
} from 'src/graphql-operations/cart-order-service/carts.mutation';
import { ClientSafeTenantConfig } from 'server/config-cache/types';
import { isNil, omitBy } from 'lodash';
import { DateTime } from 'luxon';
import { FunnelCart } from 'src/ts/types/cart';
import logger from 'logger';
import { getMetroInfo } from 'src/services/rails-service';
import {
  cleanQueryParams,
  parseAndReturnSystemParams,
} from 'src/utils/queryParams';
import {
  DEFAULT_MEAL_SIZE,
  DEFAULT_TENANT_ELIGIBILITY_CONFIG_LOGICAL_ID,
  ZIP_CODE_PARAM_KEY,
} from 'src/utils/constants';
import { getSubscriptionRecurringBooleanFromConfig } from 'src/utils/getSubscriptionRecurringBooleanFromConfig';
import { ClientNames } from '../../../utils/apolloClient';
import { getSelectedPlan } from './get-selected-plan';

/**
 * This is a helper function that either retrieves an existing cart from the API or creates
 * a new one.
 */
export const getInitializedCart = async (input: {
  apolloClient: ApolloClient;
  userContext: UserContextInterface | null;
  queryParams: ParsedUrlQuery;
  tenantConfig: ClientSafeTenantConfig;
  zipCode: string;
  cartCookieUuid?: string;
  email?: string;
  name?: string;
}): Promise<FunnelCart> => {
  const {
    apolloClient,
    queryParams,
    cartCookieUuid,
    zipCode,
    email: userSuppliedEmail,
    name: userSuppliedName,
    userContext,
  } = input;

  // First check to see if there's an existing cart that can be pulled
  const cart = await fetchSellableCart({
    apolloClient,
    zipCode,
    cartUuid: cartCookieUuid,
    previewFunnelDiscountCode: !userContext?.user,
  });

  const shouldOverrideCart = await shouldPlanOverrideCart({
    queryParams,
    cart,
  });

  if (cart && !shouldOverrideCart) {
    return cart;
  }

  // If there's no user context at this point, there's nothing we can do
  if (!userContext) {
    logger.error({
      msg: 'UnableToCreateCartWithoutUserContext',
      extra: {
        queryParams,
      },
    });

    throw new Error('UnableToCreateCartWithoutUserContext');
  }

  const {
    referralCode,
    discountCode: queryParamDiscountCode,
    email,
    name,
    tenantEligibilityConfigLogicalId,
    plan: planLogicalId,
  } = parseAndReturnSystemParams(queryParams);
  const { tenantConfig } = input;

  // Deep-linked discount code takes precedence over standing new customer discount
  const appliedDiscountedCode =
    queryParamDiscountCode || tenantConfig.standingNewCustomerDiscountCode;
  const nonEmptyFunnelDataDiscountCodes = omitBy(
    { referralCode, discountCode: appliedDiscountedCode },
    isNil,
  );

  const variables = {
    funnelData: {
      /* 
        1. User supplied email and name take precedence this is what is entered from the menu entry modal.
        2. Any existing email and name from query params.
        3. Fallback to user context email and name.
      */
      email: userSuppliedEmail || email || userContext.user?.email || '', // @TODO: These really should be optional
      name: userSuppliedName || name || userContext.user?.name || '', // @TODO: These really should be optional
      zip: zipCode,
      mealSize: DEFAULT_MEAL_SIZE,
      recurring: getSubscriptionRecurringBooleanFromConfig(
        tenantConfig.subscriptionToggleConfiguration,
      ),
      ...(nonEmptyFunnelDataDiscountCodes && {
        params: nonEmptyFunnelDataDiscountCodes,
      }),
    },
    cartData: {
      recipientName: userSuppliedName || name || userContext.user?.name || '',
      tenantEligibilityConfigLogicalId:
        tenantEligibilityConfigLogicalId ||
        DEFAULT_TENANT_ELIGIBILITY_CONFIG_LOGICAL_ID,
      ...(planLogicalId && { planLogicalId }),
      orderOn: DateTime.now().toFormat('yyyy-MM-dd'),
      ...(queryParams && {
        queryParams: cleanQueryParams(queryParams),
      }),
    },
  };

  // At this point, a cart will need to be created
  let cartCreationResponse;
  const previewDiscount = !!appliedDiscountedCode;

  if (previewDiscount) {
    cartCreationResponse = await apolloClient.mutate<
      CreateAndReturnCartWithPreviewDiscountMutation,
      CreateAndReturnCartWithPreviewDiscountMutationVariables
    >({
      mutation: CREATE_AND_RETURN_CART_WITH_PREVIEW_DISCOUNT,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
      fetchPolicy: 'no-cache',
      variables: {
        ...variables,
        previewFunnelDiscountCode: true,
      },
    });
  } else {
    cartCreationResponse = await apolloClient.mutate<
      CreateAndReturnCartMutation,
      CreateAndReturnCartMutationVariables
    >({
      mutation: CREATE_AND_RETURN_CART,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
      fetchPolicy: 'no-cache',
      variables,
    });

    if (!cartCreationResponse.data) {
      logger.error({
        msg: 'UnableToCreateCartWithError',
        extra: {
          variables,
          error: cartCreationResponse.error,
        },
      });

      throw new Error('UnableToCreateCartWithError');
    }
  }

  if (!cartCreationResponse?.data) {
    logger.error({
      msg: 'UnableToCreateCartWithError',
      extra: {
        variables,
        error: cartCreationResponse?.error,
      },
    });

    throw new Error('UnableToCreateCartWithError');
  }

  return cartCreationResponse.data.createCart;
};

/**
 * When loading pages, you'll often want to know what Zipcode to work against to load
 * catalogs or create carts. This function centralizes the logic for that work.
 */
export const getSellableZip = async (input: {
  apolloClient: ApolloClient;
  queryParams: ParsedUrlQuery;
  cartCookieUuid?: string;
  userSuppliedZipCode?: string;
}): Promise<string | undefined> => {
  const { apolloClient, queryParams, cartCookieUuid, userSuppliedZipCode } =
    input;

  // The first priority is to check if there's an user supplied zipcode cookie value. This
  // is a value that comes from the Zipcode Modal submit.
  if (userSuppliedZipCode) {
    return userSuppliedZipCode;
  }

  const cart = await fetchSellableCart({
    apolloClient,
    cartUuid: cartCookieUuid,
  });

  if (cart?.funnelData.zip) {
    return cart.funnelData.zip;
  }

  // Since there's no cart, is there a zip code in the queryParams?
  if (queryParams && queryParams[ZIP_CODE_PARAM_KEY]?.toString().trim()) {
    return (queryParams[ZIP_CODE_PARAM_KEY] as string).trim();
  }

  // Otherwise, we don't have any zipcode that can be returned.
  return undefined;
};

/**
 * When a user lands on the /menu page with the `?plan=` query param, there are situations
 * where we want to fully ignore their existing cart and create a new one. This boils down
 * to:
 *
 */
const shouldPlanOverrideCart = async (input: {
  cart: GetCartByIdQuery['cart'];
  queryParams: ParsedUrlQuery;
}): Promise<boolean> => {
  const { cart, queryParams } = input;

  const { plan: planLogicalId } = parseAndReturnSystemParams(queryParams);

  // Obviously if there's no cart, nothing to override
  if (!cart) {
    return false;
  }

  // There's no plan queryParam, nothing to override into
  if (!planLogicalId) {
    return false;
  }

  // If you've clicked the same link or refreshed the page with the same plan id,
  // there's nothing to do.
  if (planLogicalId === cart.createdFromPlan?.logicalId) {
    return false;
  }

  // Finally, ensure that this plan is something that the user has access to before
  // we destroy any existing cart.
  const verifiedPlan = await getSelectedPlan(queryParams);
  if (!verifiedPlan) {
    return false;
  }

  // At this point we have a valid plan that's new to this cart, we're assuming that the
  // user has clicked on a different plan link and wants to see a whole new cart.
  return true;
};

/**
 * Being kind of fast and loose with language here: "Sellable" in this context means that
 * the cart is in the "initialized" state and doesn't have any customizeBy error on it.
 *
 * This is the type of cart that someone can add or remove items on that they specifically
 * haven't checked out yet.
 */
const fetchSellableCart = async (input: {
  apolloClient: ApolloClient;
  zipCode?: string;
  cartUuid?: string;
  previewFunnelDiscountCode?: boolean;
}): Promise<GetCartByIdQuery['cart'] | undefined> => {
  const { apolloClient, cartUuid, zipCode, previewFunnelDiscountCode } = input;

  if (!cartUuid) {
    return;
  }

  let cart;
  try {
    if (previewFunnelDiscountCode) {
      const result = await apolloClient.query<
        GetCartByIdWithPreviewFunnelDiscountQuery,
        GetCartByIdWithPreviewFunnelDiscountQueryVariables
      >({
        query: GET_CART_BY_ID_WITH_PREVIEW_FUNNEL_DISCOUNT,
        variables: {
          uuid: cartUuid,
          previewFunnelDiscountCode: true,
        },
        context: {
          clientName: ClientNames.CART_ORDER_GRAPH,
        },
      });
      cart = result.data?.cart;
    } else {
      const result = await apolloClient.query<
        GetCartByIdQuery,
        GetCartByIdQueryVariables
      >({
        query: GET_CART_BY_ID,
        variables: {
          uuid: cartUuid,
        },
        context: {
          clientName: ClientNames.CART_ORDER_GRAPH,
        },
      });
      cart = result.data?.cart;
    }
  } catch (error) {
    logger.error({
      msg: 'ErrorFetchingCartWithUuid',
      noticable: error as Error,
      extra: {
        cartCookieUuid: cartUuid,
      },
    });

    return undefined;
  }

  // If for some reason the cart isn't found for the UUID, just return undefined
  // and let HoneyBadger know. It seems unlikely that this will ever happen without
  // the endpoint throwing an exception, but it's technically allowed by the GQL
  // definition.
  if (!cart) {
    logger.error({
      msg: 'UnableToFindCartForUUID',
      extra: {
        cartCookieUuid: cartUuid,
      },
    });

    return undefined;
  }

  if (cart.cartError?.validation?.customizeBy?.length) {
    return undefined;
  }

  if (cart.status !== CartStatus.Initialized) {
    return undefined;
  }

  // If the cart's zip is different from the provided zip, that indicates the user
  // has changed the zipcode they'd like to purchase against. In that case, we'll
  // need to create an entirely new cart
  if (zipCode && cart.funnelData['zip'] !== zipCode) {
    return undefined;
  }

  return cart;
};

export const validateZip = async (input: {
  tenantId: number;
  zip: string;
}): Promise<boolean> => {
  const { zip, tenantId } = input;

  try {
    const metroInfo = await getMetroInfo(zip, tenantId);

    // This endpoint 404's if give it a zip that the tenant doesn't serve, just being
    // extra cautious with this check.
    return !!metroInfo.allowedZips.find((metroZip) => metroZip === zip);
  } catch {
    return false;
  }
};
